import {
    IApplicationModuleSession,
    IPlatformSession,
    IPlatformViewport,
    IPlatformSessionPropertyChangedEvent,
    IUIOperationOptions, IPlatformSessionRequest, IPlatformSessionOperationEvent
} from "./PA.UIM.Core.IF.Platform";
import {ISearchRendererOptions} from "./PA.UIM.Core.IF.Search";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {IUIFramework, IUIProfile} from "./PA.UIM.Core.IF.Framework";
import {IIdentityProvider, IMessageProvider, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {UIFramework} from "./UIFramework";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import * as _ from "lodash"
import {BehaviorSubject} from "rxjs";

export class PlatformSession
    implements IPlatformSession {

    private _messageProvider: IMessageProvider;
    private _progressProvider: IProgressProvider;

    private _currentUIProfile: IUIProfile
    private _uiProfiles: IUIProfile[]

    private _identity: IIdentityProvider;
    private _defaultPlatformViewport: IPlatformViewport;
    private _framework: IUIFramework;
    private _onLoadComplete: Promise<void>;
    private _platformViewports: IPlatformViewport[];
    private _bIsPlatformSessionLoaded: boolean;
    private static _seqIdValue: number = 100

    private _OnPropertyChange: BehaviorSubject<IPlatformSessionPropertyChangedEvent>
    private _OnPlatformSessionRequest: BehaviorSubject<IPlatformSessionRequest>;
    private _OnOperation: BehaviorSubject<IPlatformSessionOperationEvent>;

    constructor() {
        this._messageProvider = new MessageProvider()
        this._progressProvider = new ProgressProvider()
        this._framework = new UIFramework()
        this._identity = new IdentityProvider({id: "PA", id_ui: "PA", name: "Performance Analyzer"})
        this._onLoadComplete = undefined
        this._platformViewports = []
        this._defaultPlatformViewport = undefined
        this._bIsPlatformSessionLoaded = false
        this._uiProfiles = []
        this._currentUIProfile = undefined
        this._OnPropertyChange = new BehaviorSubject<IPlatformSessionPropertyChangedEvent>(undefined)
        this._OnPlatformSessionRequest = new BehaviorSubject<IPlatformSessionRequest>(undefined)
        this._OnOperation = new BehaviorSubject<IPlatformSessionOperationEvent>(undefined)
    }

    get MessageProvider(): IMessageProvider {
        return this._messageProvider
    }

    get ProgressProvider(): IProgressProvider {
        return this._progressProvider
    }


    get Framework(): IUIFramework {
        return this._framework
    }

    get Identity(): IIdentityProvider {
        return this._identity
    }

    get PlatformViewports(): IPlatformViewport[] {
        return this._platformViewports
    }

    get DefaultPlatformViewport(): IPlatformViewport {
        return this._defaultPlatformViewport
    }

    get OnPlatformSessionRequest(): BehaviorSubject<IPlatformSessionRequest> {
        return this._OnPlatformSessionRequest
    }

    get OnOperation(): BehaviorSubject<IPlatformSessionOperationEvent> {
        return this._OnOperation
    }

    get bIsPlatformSessionLoaded(): boolean {
        return this._bIsPlatformSessionLoaded
    }

    get OnLoadComplete(): Promise<void> {
        const self = this
        if (self.bIsPlatformSessionLoaded) {
            return Promise.resolve()
        }
        if (!_.isUndefined(self._onLoadComplete)) {
            return self._onLoadComplete
        }

        //--
        let returnValue = this._onLoadComplete = new Promise<void>((resolve, error) => {
            let defaultProfile = window.localStorage.getItem("profile")

            self._framework.OnLoadComplete
                .then((ignore) => {
                    //--
                    self._uiProfiles = self._framework.UIProfiles || []
                    if (!_.isEmpty(defaultProfile)){
                        self._currentUIProfile = _.first(_.filter(self._uiProfiles, aProfile=>aProfile.wb_id==Number(defaultProfile)))
                    }

                    if (_.isEmpty(self._currentUIProfile))
                        self._currentUIProfile = _.first(_.sortBy(self._uiProfiles, ['sort']))

                    self._bIsPlatformSessionLoaded = true
                    self._onLoadComplete = undefined
                    resolve()
                })
                .catch((err) => {
                    self.ProgressProvider.exception(`Platform initialization error: ${err}`)
                    self._bIsPlatformSessionLoaded = false
                    self._onLoadComplete = undefined
                    error(err)
                })

        })

        return returnValue
    }

    get CurrentUIProfile(): IUIProfile {
        return this._currentUIProfile
    }

    set CurrentUIProfile(value) {
        this._currentUIProfile = value
        this.notifyPropertyChange("CurrentUIProfile", value)
    }

    get UIProfiles(): IUIProfile[] {
        return this._uiProfiles
    }

    get OnPropertyChange(): BehaviorSubject<IPlatformSessionPropertyChangedEvent> {
        return this._OnPropertyChange
    }

    private notifyPropertyChange(propertyName: string, value: any) {
        this._OnPropertyChange.next({platformSession: this, propertyName: propertyName, propertyValue: value})
    }

    private getViewports(options?: IUIOperationOptions): IPlatformViewport[] {
        const self = this
        let opts: IUIOperationOptions = options || {}
        let returnValue: IPlatformViewport[] = []
        let viewPortId = opts.viewPort?.viewportId || opts.viewPortId
        if (_.isEmpty(viewPortId)) {
            return this._platformViewports
        }

        _.forEach(self._platformViewports, (avp) => {
            if (avp.viewportId == viewPortId) {
                returnValue.push(avp)
            }
        })

        if (returnValue.length) {
            return returnValue
        }

        return self._platformViewports
    }

    private getSingleOperationViewport(options?: IUIOperationOptions): IPlatformViewport {
        const self = this
        let opts: IUIOperationOptions = options || {}
        let viewPortId = opts.viewPort?.viewportId || opts.viewPortId
        if (_.isEmpty(viewPortId)) {
            return this._defaultPlatformViewport
        }

        _.forEach(self._platformViewports, (avp) => {
            if (avp.viewportId == viewPortId) {
                return avp
            }
        })

        return self._defaultPlatformViewport
    }

    Create(anExecutableId: string, options?: IUIOperationOptions): IApplicationModuleSession {
        const self = this
        let aVP = self.getSingleOperationViewport(options)
        if (_.isEmpty(aVP)) {
            return undefined
        }
        let aModuleSession = aVP.Create(anExecutableId, options)
        return aModuleSession
    }

    Open(anExecutableId: string, BOid: string | number, options?: IUIOperationOptions): IApplicationModuleSession {
        const self = this
        let aVP = self.getSingleOperationViewport(options)
        if (_.isEmpty(aVP)) {
            return undefined
        }
        return aVP.Open(anExecutableId, BOid, options)
    }

    OpenBO(anExecutableId: string, aBO: IBusinessObject, options?: IUIOperationOptions): IApplicationModuleSession {
        const self = this
        let aVP = self.getSingleOperationViewport(options)
        if (_.isEmpty(aVP)) {
            return undefined
        }
        return aVP.OpenBO(anExecutableId, aBO, options)
    }

    async Close(anExecutableId: string, BOid: string | number, options?: IUIOperationOptions): Promise<boolean> {
        const self = this
        let opts: IUIOperationOptions = options || {}

        let returnValue = false
        for (let aViewport of self.getViewports(opts)) {
            returnValue ||= await aViewport.Close(anExecutableId, BOid, options)
        }
        return returnValue
    }


    async SelectOne(anExecutableId: string, options?: ISearchRendererOptions): Promise<number | string> {
        const self = this
        let aVP = self.getSingleOperationViewport(options)
        if (!_.isEmpty(aVP)) {
            return Promise.resolve(0)
        }
        return aVP.SelectOne(anExecutableId, options)
    }

    async SelectMany(anExecutableId: string, options?: ISearchRendererOptions): Promise<number[] | string[]> {
        const self = this
        let aVP = self.getSingleOperationViewport(options)
        if (!_.isEmpty(aVP)) {
            return Promise.resolve([])
        }
        return aVP.SelectMany(anExecutableId, options)
    }

    async SelectNavigationList(anExecutableId: string, options?: ISearchRendererOptions): Promise<number[] | string[]> {
        const self = this
        let aVP = self.getSingleOperationViewport(options)
        if (!_.isEmpty(aVP)) {
            return Promise.resolve([])
        }
        return aVP.SelectNavigationList(anExecutableId, options)
    }


    registerViewport(viewport: IPlatformViewport): void {
        const self = this
        if (_.isEmpty(viewport)) {
            return
        }

        let bFound = false
        _.forEach(self._platformViewports, (avp) => {
            if (avp == viewport) {
                bFound = true
                return false
            }
        })

        if (!bFound) {
            self._platformViewports.push(viewport)
        }

        if (_.isEmpty(self._defaultPlatformViewport)) {
            self.setDefaultViewport(viewport)
            self.notifyPropertyChange("DefaultPlatformViewport", viewport)

        }

        self.notifyPropertyChange("PlatformViewports", self.PlatformViewports)

    }

    unRegisterViewport(viewport: IPlatformViewport): void {
        const self = this
        if (_.isEmpty(viewport)) {
            return
        }

        let removed = _.remove(self._platformViewports, viewport)

        if (_.includes(removed, self._defaultPlatformViewport)) {
            if (self._platformViewports.length) {
                self.setDefaultViewport(_.first(self._platformViewports))
            } else {
                self._defaultPlatformViewport = undefined
            }
        }
        self.notifyPropertyChange("PlatformViewports", self.PlatformViewports)
    }

    setDefaultViewport(viewport: IPlatformViewport): void {
        const self = this
        if (_.isEmpty(viewport)) {
            return
        }

        if (_.includes(self._platformViewports, viewport)) {
            self._defaultPlatformViewport = viewport
        } else {
            console.log(`Viewport (${viewport?.viewportId}) not registered. `)
        }

    }


    static getNextSessionId(): number {
        PlatformSession._seqIdValue += 1
        return PlatformSession._seqIdValue;
    }


}
